import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { usePlanFeatures } from './hooks/usePlanFeatures';
import { useSessionTracking } from './hooks/useSessionTracking';
import { usePageTracking } from './hooks/usePageTracking';
import Layout from './components/layout/Layout';
import AuthLayout from './components/layout/AuthLayout';
import SubscriptionGuard from './components/SubscriptionGuard';
import SubscriptionBanner from './components/SubscriptionBanner';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import GroupsPage from './pages/GroupsPage';
import GroupDetailsPage from './pages/GroupDetailsPage';
import CreateGroupPage from './pages/CreateGroupPage';
import PlayersPage from './pages/PlayersPage';
import PlayerProfilePage from './pages/PlayerProfilePage';
import PlayerResenhasPage from './pages/PlayerResenhasPage';
import EditPlayerPage from './pages/EditPlayerPage';
import FinancesPage from './pages/FinancesPage';
import SorteioPage from './pages/SorteioPage';
import ResenhaPage from './pages/ResenhaPage';
import ResenhaFormPage from './pages/ResenhaFormPage';
import ResenhaDetailsPage from './pages/ResenhaDetailsPage';
import ResenhaEditPage from './pages/ResenhaEditPage';
import ProfilePage from './pages/ProfilePage';
import GamesManagementPage from './pages/GamesManagementPage';
import RemindersPage from './pages/RemindersPage';
import RewardsPage from './pages/RewardsPage';
import AboutPage from './pages/AboutPage';
import HelpPage from './pages/HelpPage';
import NotFoundPage from './pages/NotFoundPage';
import LoadingScreen from './components/common/LoadingScreen';
import NotificationManager from './components/notifications/NotificationManager';
import PricingPage from './pages/PricingPage';
import SuccessPage from './pages/SuccessPage';
import { StripeProvider } from './contexts/StripeContext';
import AdminGuard from './components/AdminGuard';
import AdminLayout from './components/layout/AdminLayout';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import UsersManagementPage from './pages/admin/UsersManagementPage';
import UserDetailsPage from './pages/admin/UserDetailsPage';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import SubscriptionsPage from './pages/admin/SubscriptionsPage';
import CommunicationPage from './pages/admin/CommunicationPage';
import AdminLogsPage from './pages/admin/AdminLogsPage';
import PlatformSettingsPage from './pages/admin/PlatformSettingsPage';
import UpdateUserPlanPage from './pages/admin/UpdateUserPlanPage';
import UserPlansPage from './pages/admin/UserPlansPage';
import FixMissingProfilesPage from './pages/admin/FixMissingProfilesPage';
import GameConfirmationPage from './pages/GameConfirmationPage';
import LineupPage from './pages/LineupPage';
import PixTestPage from './pages/PixTestPage';
import PixPaymentPage from './pages/PixPaymentPage';
import PublicPlayerRegistrationPage from './pages/PublicPlayerRegistrationPage';

const App: React.FC = () => {
  const { user, isLoading } = useAuth();
  const { limits } = usePlanFeatures();
  const location = useLocation();

  // Enable analytics tracking
  const { sessionId } = useSessionTracking();
  usePageTracking(sessionId);

  if (isLoading) {
    return <LoadingScreen />;
  }

  // List of public routes that don't require authentication
  const publicRoutes = ['/login', '/cadastro', '/pricing', '/success', '/esqueci-senha', '/redefinir-senha', '/admin/login'];

  // Check if current route is a confirmation page
  const isConfirmationRoute = /^\/confirmar\/[^/]+$/.test(location.pathname);
  const isPixPaymentRoute = /^\/pix\/[^/]+$/.test(location.pathname);
  const isPlayerRegistrationRoute = /^\/cadastrar-jogador\/[^/]+$/.test(location.pathname);

  const isPublicRoute = publicRoutes.includes(location.pathname) ||
                       isConfirmationRoute ||
                       isPixPaymentRoute ||
                       isPlayerRegistrationRoute;

  // If not logged in and not on a public route, redirect to login
  if (!user && !isPublicRoute) {
    return <Navigate to="/login" replace />;
  }

  return (
    <StripeProvider>
      <>
        {user && <NotificationManager />}
        {user && <SubscriptionBanner />}
        <Routes>
          {/* Public routes */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/" replace />} />
            <Route path="/cadastro" element={!user ? <RegisterPage /> : <Navigate to="/" replace />} />
            <Route path="/esqueci-senha" element={!user ? <ForgotPasswordPage /> : <Navigate to="/" replace />} />
            <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/success" element={<SuccessPage />} />
            <Route path="/confirmar/:token" element={<GameConfirmationPage />} />
            <Route path="/pix/:token" element={<PixPaymentPage />} />
            <Route path="/cadastrar-jogador/:token" element={<PublicPlayerRegistrationPage />} />
            <Route path="/admin/login" element={!user ? <AdminLoginPage /> : <Navigate to="/admin" replace />} />
          </Route>

          {/* Admin routes */}
          <Route element={<AdminGuard><AdminLayout /></AdminGuard>}>
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/users" element={<UsersManagementPage />} />
            <Route path="/admin/users/:id" element={<UserDetailsPage />} />
            <Route path="/admin/analytics" element={<AnalyticsPage />} />
            <Route path="/admin/subscriptions" element={<SubscriptionsPage />} />
            <Route path="/admin/communication" element={<CommunicationPage />} />
            <Route path="/admin/logs" element={<AdminLogsPage />} />
            <Route path="/admin/settings" element={<PlatformSettingsPage />} />
            <Route path="/admin/update-user" element={<UpdateUserPlanPage />} />
            <Route path="/admin/user-plans" element={<UserPlansPage />} />
            <Route path="/admin/fix-profiles" element={<FixMissingProfilesPage />} />
            <Route path="/admin/pix-test" element={<PixTestPage />} />
          </Route>

          {/* Protected routes */}
          <Route element={<Layout />}>
            <Route
              path="/"
              element={
                <SubscriptionGuard>
                  <DashboardPage />
                </SubscriptionGuard>
              }
            />
            <Route
              path="/grupos"
              element={
                <SubscriptionGuard>
                  <GroupsPage />
                </SubscriptionGuard>
              }
            />
            <Route
              path="/grupos/novo"
              element={
                <SubscriptionGuard>
                  <CreateGroupPage />
                </SubscriptionGuard>
              }
            />
            <Route
              path="/grupos/:id"
              element={
                <SubscriptionGuard>
                  <GroupDetailsPage />
                </SubscriptionGuard>
              }
            />
            <Route
              path="/jogadores"
              element={
                <SubscriptionGuard>
                  <PlayersPage />
                </SubscriptionGuard>
              }
            />
            <Route
              path="/jogadores/:id"
              element={
                <SubscriptionGuard>
                  <PlayerProfilePage />
                </SubscriptionGuard>
              }
            />
            <Route
              path="/jogadores/:id/resenhas"
              element={
                <SubscriptionGuard>
                  <PlayerResenhasPage />
                </SubscriptionGuard>
              }
            />
            <Route
              path="/jogadores/:id/editar"
              element={
                <SubscriptionGuard>
                  <EditPlayerPage />
                </SubscriptionGuard>
              }
            />
            <Route
              path="/financeiro"
              element={
                <SubscriptionGuard>
                  <FinancesPage />
                </SubscriptionGuard>
              }
            />
            <Route
              path="/sorteio"
              element={
                <SubscriptionGuard>
                  <SorteioPage />
                </SubscriptionGuard>
              }
            />
            <Route
              path="/jogos"
              element={
                <SubscriptionGuard>
                  <GamesManagementPage />
                </SubscriptionGuard>
              }
            />
            <Route
              path="/escalacao"
              element={
                <SubscriptionGuard>
                  <LineupPage />
                </SubscriptionGuard>
              }
            />

            {limits.hasResenha && (
              <>
                <Route
                  path="/resenha"
                  element={
                    <SubscriptionGuard requireSubscription={true}>
                      <ResenhaPage />
                    </SubscriptionGuard>
                  }
                />
                <Route
                  path="/resenha/nova"
                  element={
                    <SubscriptionGuard requireSubscription={true}>
                      <ResenhaFormPage />
                    </SubscriptionGuard>
                  }
                />
                <Route
                  path="/resenha/:id"
                  element={
                    <SubscriptionGuard requireSubscription={true}>
                      <ResenhaDetailsPage />
                    </SubscriptionGuard>
                  }
                />
                <Route
                  path="/resenha/:id/editar"
                  element={
                    <SubscriptionGuard requireSubscription={true}>
                      <ResenhaEditPage />
                    </SubscriptionGuard>
                  }
                />
              </>
            )}

            <Route
              path="/perfil"
              element={
                <SubscriptionGuard>
                  <ProfilePage />
                </SubscriptionGuard>
              }
            />
            <Route
              path="/lembretes"
              element={
                <SubscriptionGuard>
                  <RemindersPage />
                </SubscriptionGuard>
              }
            />
            <Route
              path="/recompensas"
              element={
                <SubscriptionGuard>
                  <RewardsPage />
                </SubscriptionGuard>
              }
            />
            <Route
              path="/sobre"
              element={
                <SubscriptionGuard>
                  <AboutPage />
                </SubscriptionGuard>
              }
            />
            <Route
              path="/ajuda"
              element={
                <SubscriptionGuard>
                  <HelpPage />
                </SubscriptionGuard>
              }
            />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </>
    </StripeProvider>
  );
};

export default App;